import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  MessageCircle,
} from 'lucide-react';
import Navbar from '../common/Navbar';
import Footer from '../common/Footer';
import SEO from '../common/SEO';
import { useAuth } from '../../contexts/AuthContext';
import { validateContactInput, submitContactMessage } from '../../lib/contactService';
import { faqs as allFaqs } from '../../lib/faqData';
import sendMessageSheet from '../../assets/contact/send-message-sheet.webp';
import '../../styles/ContactPage.css';

const WA_LINK = 'https://wa.me/916386568997';
const IG_LINK = 'https://www.instagram.com/senritsux.in/';

// 4 Authoritative Reference FAQs from 03.webp
const CONTACT_FAQS = [
  {
    q: 'How fast will my track be live on streaming platforms?',
    a: 'Once submitted, our quality assurance team reviews your audio metadata and artwork within 24 to 48 hours. Most releases go live on Spotify and Apple Music within 2 to 5 business days. For planned release dates, we recommend submitting at least 2 weeks in advance to allow playlist editorial pitching.',
    slug: 'how-long-release-go-live',
  },
  {
    q: 'Do I retain 100% of my music copyright and master rights?',
    a: 'YES! Absolutely 100%. SenritsuX is strictly an artist-first ecosystem. You never sign away your master rights or publishing copyright. You have complete freedom to remove, transfer, or re-license your releases at any time without punitive exit fees.',
    slug: 'retain-100-master-rights',
  },
  {
    q: 'How and when do I receive my royalty payouts?',
    a: 'Depending on your distribution plan, you keep up to 97.5% of all streaming and download royalties earned across all platforms. Payouts are updated monthly as DSP reporting arrives and can be withdrawn directly via UPI, Indian Netbanking (NEFT/IMPS), or International wire transfer without unnecessary holding periods.',
    slug: 'royalty-payouts',
  },
  {
    q: 'How does playlist pitching work?',
    a: 'When you submit releases at least 10 to 14 days ahead of release date, our team pitches your track to Spotify, Apple Music, and Amazon Music editorial curators, along with our verified independent curator network.',
    slug: 'playlist-pitching',
  },
];

const STOP_WORDS = new Set([
  'the', 'is', 'in', 'at', 'of', 'on', 'and', 'a', 'an', 'to', 'for', 'with', 'from',
  'by', 'about', 'as', 'into', 'like', 'through', 'after', 'over', 'between', 'out',
  'against', 'during', 'without', 'before', 'under', 'around', 'among', 'i', 'my', 'me',
  'we', 'our', 'you', 'your', 'he', 'she', 'it', 'they', 'them', 'what', 'which', 'who',
  'whom', 'whose', 'when', 'where', 'why', 'how', 'all', 'any', 'both', 'each', 'few',
  'more', 'most', 'other', 'some', 'such', 'no', 'nor', 'not', 'only', 'own', 'same',
  'so', 'than', 'too', 'very', 'can', 'will', 'just', 'should', 'now', 'am', 'are', 'was',
  'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'hello', 'hi',
  'hey', 'team', 'please', 'want', 'need', 'know', 'tell', 'help', 'sir', 'madam',
]);

const KEYWORD_SYNONYMS = {
  money: ['royalty', 'payout', 'earnings', 'revenue', 'payouts'],
  earnings: ['royalty', 'payout', 'royalties', 'payouts'],
  revenue: ['royalty', 'payout', 'royalties'],
  payment: ['payout', 'royalty', 'upi', 'bank', 'payouts'],
  payments: ['payout', 'royalty', 'upi', 'bank', 'payouts'],
  paid: ['payout', 'royalty'],
  pay: ['payout', 'royalty'],
  withdraw: ['payout', 'withdrawal', 'upi', 'netbanking'],
  withdrawal: ['payout', 'upi', 'netbanking'],
  bank: ['payout', 'netbanking', 'wire', 'upi'],
  timeline: ['how long', 'release go live', 'days', 'time'],
  timing: ['how long', 'release go live', 'days'],
  delay: ['how long', 'release go live', 'days'],
  duration: ['how long', 'days', 'hours'],
  schedule: ['release go live', 'advance', 'how long', 'date'],
  speed: ['how fast', 'how long', 'release go live'],
  fast: ['how fast', 'how long', '24 to 48 hours'],
  quick: ['how fast', 'how long'],
  when: ['how long', 'release go live', 'days'],
  distribute: ['distribution', 'streaming platforms', 'deliver'],
  distributing: ['distribution', 'streaming platforms'],
  stream: ['streaming platforms', 'spotify', 'apple music'],
  stores: ['streaming platforms', 'digital stores', 'spotify'],
  dsp: ['streaming platforms', 'dsp', 'spotify', 'apple music'],
  pitch: ['playlist pitching', 'editorial curators', 'curator'],
  pitching: ['playlist pitching', 'editorial curators'],
  curator: ['playlist pitching', 'curators', 'editorial'],
  curators: ['playlist pitching', 'curators', 'editorial'],
  playlist: ['playlist pitching', 'curators'],
  playlists: ['playlist pitching', 'curators'],
  promotion: ['playlist pitching', 'editorial promotion'],
  promote: ['playlist pitching', 'editorial promotion'],
  rights: ['master rights', 'copyright', 'ownership'],
  copyright: ['master rights', 'copyright', 'publishing'],
  ownership: ['master rights', 'copyright', 'retain'],
  legal: ['master rights', 'copyright', 'license'],
  contract: ['master rights', 'copyright', 'retain'],
  license: ['master rights', 're-license', 'copyright'],
  artwork: ['audio and artwork formats', '3000x3000', 'jpeg', 'png', 'square'],
  image: ['artwork', '3000x3000', 'jpeg', 'png'],
  photo: ['artwork', '3000x3000'],
  audio: ['audio and artwork formats', 'stereo wav', '44.1 khz'],
  format: ['audio and artwork formats', 'wav', 'jpeg'],
  formats: ['audio and artwork formats', 'wav', 'jpeg'],
  specs: ['audio and artwork formats', '16-bit', '3000x3000'],
  wav: ['audio and artwork formats', 'stereo wav'],
  cost: ['hidden fees', 'annual takedown penalties', 'fees'],
  costs: ['hidden fees', 'fees'],
  pricing: ['hidden fees', 'fees', 'plan'],
  price: ['hidden fees', 'fees'],
  fee: ['hidden fees', 'fees', 'charges'],
  fees: ['hidden fees', 'penalties'],
  charge: ['hidden fees', 'charges', 'fees'],
  charges: ['hidden fees', 'fees'],
  badge: ['verified star badges', 'artist profile'],
  badges: ['verified star badges', 'artist profile'],
  verify: ['verified star badges'],
  verification: ['verified star badges'],
  verified: ['verified star badges', 'profile'],
  sample: ['cover songs remixes tracks with samples', 'samples'],
  samples: ['cover songs remixes tracks with samples', 'samples'],
  sampling: ['cover songs remixes tracks with samples'],
  remix: ['cover songs remixes tracks with samples'],
  remixes: ['cover songs remixes tracks with samples'],
  network: ['artist web directory', 'collaboration'],
  collab: ['artist web directory', 'collaboration'],
  collaboration: ['artist web directory', 'producers'],
  collaborations: ['artist web directory', 'producers'],
  directory: ['artist web directory'],
};

function getContextualFaqSuggestions(subject = '', message = '') {
  const combined = (String(subject || '') + ' ' + String(message || '')).toLowerCase().trim();
  if (combined.length < 3) return [];

  const rawTokens = combined
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length >= 3 && !STOP_WORDS.has(t));

  if (rawTokens.length === 0) return [];

  const searchTerms = new Set();
  rawTokens.forEach((tok) => {
    searchTerms.add(tok);
    if (KEYWORD_SYNONYMS[tok]) {
      KEYWORD_SYNONYMS[tok].forEach((syn) => searchTerms.add(syn));
    }
    if (tok.length > 5) {
      if (tok.endsWith('ies')) searchTerms.add(tok.slice(0, -3));
      else if (tok.endsWith('ing')) searchTerms.add(tok.slice(0, -3));
      else if (tok.endsWith('ed')) searchTerms.add(tok.slice(0, -2));
      else if (tok.endsWith('s')) searchTerms.add(tok.slice(0, -1));
    }
  });

  const termList = Array.from(searchTerms);

  const scored = allFaqs.map((faq) => {
    const qLower = faq.question.toLowerCase();
    const aLower = faq.answer.toLowerCase();
    const catLower = faq.category.toLowerCase();
    let score = 0;

    termList.forEach((term) => {
      if (qLower.includes(term)) score += 8;
      else if (catLower.includes(term)) score += 4;
      else if (aLower.includes(term)) score += 2;
    });

    return { faq, score };
  });

  return scored
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((item) => item.faq);
}

export default function ContactPage() {
  const { user, profile } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'Singer / Rapper',
    subject: '',
    message: '',
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Accordion state
  const [openFaqs, setOpenFaqs] = useState({});

  // Contextual FAQ suggestions state
  const [suggestedFaqs, setSuggestedFaqs] = useState([]);
  const [expandedSuggestionSlug, setExpandedSuggestionSlug] = useState(null);

  // Auto-populate user details when authenticated
  useEffect(() => {
    if (user || profile) {
      setFormData((prev) => ({
        ...prev,
        name: prev.name || profile?.stage_name || profile?.name || '',
        email: prev.email || user?.email || '',
      }));
    }
  }, [user, profile]);

  // Dynamically update contextual FAQ suggestions as user types Subject or Message
  useEffect(() => {
    const matches = getContextualFaqSuggestions(formData.subject, formData.message);
    setSuggestedFaqs(matches);
    if (expandedSuggestionSlug && !matches.some((m) => m.slug === expandedSuggestionSlug)) {
      setExpandedSuggestionSlug(null);
    }
  }, [formData.subject, formData.message, expandedSuggestionSlug]);

  const toggleFaq = (index) => {
    setOpenFaqs((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleSuggestionClick = (faq) => {
    setExpandedSuggestionSlug((prev) => (prev === faq.slug ? null : faq.slug));
    const leftIdx = CONTACT_FAQS.findIndex((f) => f.slug === faq.slug);
    if (leftIdx >= 0) {
      setOpenFaqs((prev) => ({ ...prev, [leftIdx]: true }));
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: null }));
    }
    if (formError) setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setFormError('');
    setFieldErrors({});

    const validation = validateContactInput(formData);
    if (!validation.valid) {
      const errMap = {};
      if (!formData.name || formData.name.trim().length < 2) {
        errMap.name = 'Please enter your name (at least 2 characters).';
      }
      if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        errMap.email = 'Please enter a valid email address.';
      }
      if (!formData.message || formData.message.trim().length < 10) {
        errMap.message = 'Please enter a message (at least 10 characters).';
      }
      setFieldErrors(errMap);
      setFormError(validation.error || 'Please correct the highlighted fields.');
      return;
    }

    setSubmitting(true);

    try {
      const res = await submitContactMessage(formData, { userId: user?.id });
      if (res.success) {
        setSubmitted(true);
        setFormData({
          name: profile?.stage_name || profile?.name || '',
          email: user?.email || '',
          role: 'Singer / Rapper',
          subject: '',
          message: '',
        });
        setFieldErrors({});
      } else {
        setFormError(res.error || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      setFormError('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sx-contact-root">
      <SEO
        title="Contact Us | SenritsuX Support & Artist Relations"
        description="Have questions regarding distribution, custom campaigns, beat sales, or creator tools? Reach out to our artist relations team directly."
        canonical="/contact"
      />
      <Navbar />

      <main className="sx-contact-main">
        <div className="sx-contact-container">
          {/* ================================================================
              LEFT COLUMN: Heading & Frequently Asked Questions (Reference 03.webp)
              ================================================================ */}
          <section className="sx-contact-left-col" aria-label="Support Information & FAQ">
            <div className="sx-contact-header-block">
              <h1 className="sx-contact-title">
                We&apos;re Here to <br />
                <span className="sx-contact-title-accent">Support Your Art</span>
              </h1>
              <p className="sx-contact-subtitle">
                Have questions regarding distribution, custom campaigns, beat sales, or creator tools? Reach out to our artist relations team directly.
              </p>
            </div>

            {/* Contextual FAQ Suggestions Panel (Above FAQ section, exactly same width) */}
            {suggestedFaqs.length > 0 && (
              <div
                className="sx-contact-suggestions-card"
                role="region"
                aria-label="Contextual FAQ Suggestions"
              >
                <div className="sx-contact-suggestions-header">
                  <h3 className="sx-contact-suggestions-title">This might help</h3>
                </div>
                <div className="sx-contact-suggestions-list">
                  {suggestedFaqs.map((faq) => {
                    const isExpanded = expandedSuggestionSlug === faq.slug;
                    return (
                      <div key={faq.slug} className="sx-contact-suggestion-item">
                        <button
                          type="button"
                          onClick={() => handleSuggestionClick(faq)}
                          className={`sx-contact-suggestion-btn ${isExpanded ? 'active' : ''}`}
                          aria-expanded={isExpanded}
                        >
                          <span className="sx-contact-suggestion-q">{faq.question}</span>
                          <span className="sx-contact-suggestion-chevron">
                            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                          </span>
                        </button>

                        {isExpanded && (
                          <div className="sx-contact-suggestion-ans">
                            <p>{faq.answer}</p>
                            <div style={{ marginTop: '0.45rem' }}>
                              <Link
                                to={`/faq/${faq.slug}`}
                                className="sx-contact-suggestion-more"
                              >
                                Learn more in FAQ <ArrowRight size={12} />
                              </Link>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="sx-contact-faq-card">
              <h2 className="sx-contact-faq-title">Frequently Asked Questions</h2>
              <div className="sx-contact-faq-list">
                {CONTACT_FAQS.map((faq, idx) => {
                  const isOpen = !!openFaqs[idx];
                  return (
                    <div key={faq.slug} className="sx-contact-faq-item">
                      <button
                        type="button"
                        onClick={() => toggleFaq(idx)}
                        className="sx-contact-faq-btn"
                        aria-expanded={isOpen}
                        aria-controls={`faq-ans-${idx}`}
                      >
                        <span className="sx-contact-faq-qtext">{faq.q}</span>
                        <span className="sx-contact-faq-chevron">
                          {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </span>
                      </button>

                      {isOpen && (
                        <div id={`faq-ans-${idx}`} className="sx-contact-faq-answer">
                          <p>{faq.a}</p>
                          <div style={{ marginTop: '0.45rem' }}>
                            <Link
                              to={`/faq/${faq.slug}`}
                              className="sx-contact-faq-more-link"
                            >
                              Learn more in FAQ <ArrowRight size={12} />
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="sx-contact-faq-more">
                <Link to="/faq" className="sx-contact-faq-more-link">
                  View all FAQs <ArrowRight size={13} />
                </Link>
              </div>
            </div>
          </section>

          {/* ================================================================
              RIGHT COLUMN: Send Message Sheet Card (Artwork + Controls)
              ================================================================ */}
          <section className="sx-contact-right-col" aria-label="Send Us a Direct Message">
            <div className="sx-contact-sheet-wrapper">
              {/* Authentic Send Message Sheet Artwork */}
              <img
                src={sendMessageSheet}
                alt="Send Us a Direct Message Sheet"
                className="sx-contact-sheet-art"
                loading="eager"
              />

              {/* Clickable Social Badges (Instagram & WhatsApp) */}
              <div className="sx-contact-social-overlay" aria-label="Direct Social Channels">
                <a
                  href={IG_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sx-contact-social-link"
                  aria-label="SenritsuX on Instagram (Opens in new tab)"
                  title="Instagram DM"
                />
                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sx-contact-social-link"
                  aria-label="SenritsuX on WhatsApp (Opens in new tab)"
                  title="WhatsApp Support"
                />
              </div>

              {/* Success Confirmation State */}
              {submitted ? (
                <div className="sx-contact-success-overlay" role="status">
                  <CheckCircle2 size={44} className="sx-contact-success-icon" />
                  <h3 className="sx-contact-success-title">Message Received!</h3>
                  <p className="sx-contact-success-desc">
                    Message sent successfully. Our artist relations team will get back to you within 24 hours.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="sx-contact-reset-btn"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                /* Functional Contact Form */
                <form onSubmit={handleSubmit} noValidate className="sx-contact-form-overlay">
                  {/* Error Alert Banner */}
                  {formError && (
                    <div className="sx-contact-alert sx-contact-alert-error" role="alert">
                      <AlertTriangle size={14} style={{ flexShrink: 0 }} />
                      <span>{formError}</span>
                    </div>
                  )}

                  {/* Row 1: Your Name * & Your Email * */}
                  <div className="sx-contact-slot-name">
                    <label htmlFor="contact-name" className="visually-hidden">Your Name *</label>
                    <input
                      id="contact-name"
                      type="text"
                      required
                      placeholder="e.g. Arjun Sharma"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      disabled={submitting}
                      className={`sx-contact-input ${fieldErrors.name ? 'has-error' : ''}`}
                      aria-required="true"
                    />
                  </div>

                  <div className="sx-contact-slot-email">
                    <label htmlFor="contact-email" className="visually-hidden">Your Email *</label>
                    <input
                      id="contact-email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      disabled={submitting}
                      className={`sx-contact-input ${fieldErrors.email ? 'has-error' : ''}`}
                      aria-required="true"
                    />
                  </div>

                  {/* Row 2: Your Creator Role & Subject */}
                  <div className="sx-contact-slot-role">
                    <label htmlFor="contact-role" className="visually-hidden">Your Creator Role</label>
                    <select
                      id="contact-role"
                      value={formData.role}
                      onChange={(e) => handleChange('role', e.target.value)}
                      disabled={submitting}
                      className="sx-contact-select"
                    >
                      <option value="Singer / Rapper">Singer / Rapper</option>
                      <option value="Music Producer">Music Producer</option>
                      <option value="Video Editor">Video Editor</option>
                      <option value="Graphic Designer">Graphic Designer</option>
                      <option value="Record Label / Manager">Record Label / Manager</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="sx-contact-slot-subject">
                    <label htmlFor="contact-subject" className="visually-hidden">Subject</label>
                    <input
                      id="contact-subject"
                      type="text"
                      placeholder="e.g. Distribution Inquiry"
                      value={formData.subject}
                      onChange={(e) => handleChange('subject', e.target.value)}
                      disabled={submitting}
                      className={`sx-contact-input ${fieldErrors.subject ? 'has-error' : ''}`}
                    />
                  </div>

                  {/* Row 3: Message * */}
                  <div className="sx-contact-slot-message">
                    <label htmlFor="contact-message" className="visually-hidden">Message *</label>
                    <textarea
                      id="contact-message"
                      required
                      placeholder="Tell us about your project, release schedule, or question..."
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      disabled={submitting}
                      className={`sx-contact-textarea ${fieldErrors.message ? 'has-error' : ''}`}
                      aria-required="true"
                    />
                  </div>

                  {/* Row 4: Send Message Button */}
                  <div className="sx-contact-slot-submit">
                    <button
                      type="submit"
                      disabled={submitting}
                      className={`sx-contact-btn ${submitting ? 'submitting' : ''}`}
                      aria-label="Send Message"
                    >
                      {submitting ? (
                        <>
                          <span className="sx-contact-spinner" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <span>Send Message</span>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
